import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BarraDeLista, BuscaDaBarra, FiltrosAtivos, RodapeDaLista, usePaginacao, type FiltroAtivo,
} from '../BarraDeLista';
import { Periodo } from '../Periodo';
import { Link, useNavigate } from 'react-router-dom';
import { api, ErroApi } from '../api';
import { clienteEObra, dataHora, plural, rotulo } from '../formato';
import { LeitorQr } from '../LeitorQr';
import {
  Seletor, SeletorDeCliente, type OpcaoDeFiltro,
} from '../Filtros';
import { useSessao } from '../sessao';
import { Carregando, Dialogo, ErroDaApi, Vazio } from '../ui';
import { LigarPrevistas } from './LigarPrevistas';
import {
  CamposDaFicha, TabelaComFicha, type ColunaComFicha, type FichaDaLinha,
} from '../TabelaComFicha';

/**
 * Uma OS que ainda espera amostra (migração `0148`).
 *
 * «Ao utilizar a tela de recebimento de amostras, deve apresentar um
 *  resumo das OS que ainda não tiveram a chegada de amostras. Assim ao
 *  chegar uma carga de amostras o laboratorista facilmente saberá qual
 *  OS tem amostras pendentes de recebimento. E terá um botão Receber
 *  amostras para cada OS em aberto» — Testesolo, 02/09/2026.
 */
interface OsEsperando {
  os_id: string;
  os_numero: string;
  cliente: string | null;
  obra_nome: string | null;
  prazo_contratual: string | null;
  dias_para_prazo: number | null;
  dias_aberta: number;
  previstas: number;
  atendidas: number;
  faltam: number;
  recebidas: number;
  motivo: 'sem_nenhuma' | 'previsao_aberta';
  /*
   * Onda 250 (`RN-219`, 0225): as recebidas SEM ligação a uma prevista, e
   * quantos pares o nome resolve. É a falta que é de ligação, não de
   * chegada — a OS 2610364 dizia «1 de 1 prevista ainda não chegaram»
   * com o bloco na prateleira.
   */
  soltas: number;
  a_ligar: number;
}

interface Lote {
  id: string;
  codigo: string;
  decisao: string;
  datahora_recebimento: string;
  cliente: string | null;
  obra_nome: string | null;
  os_numero: string | null;
  responsavel_entrega_nome: string | null;
  amostras: string;
  evidencias: string;
  amostras_com_roteiro: string;
  amostras_sem_minimo?: string;
  pdf_revisao: number;
  status: string;
  aprovado: boolean;
}

/*
 * A situação do recebimento, na lista suspensa (Testesolo, 02/09/2026).
 *
 * São DUAS colunas no banco — `status` (em recebimento / concluído) e
 * `decisao` (aceite / com ressalva / rejeição) — mas uma pergunta só
 * para quem olha a lista: «em que pé está este lote?». O valor carrega
 * o prefixo para a tela saber qual parâmetro mandar.
 */
const SITUACOES: OpcaoDeFiltro[] = [
  { valor: 'status:em_recebimento', texto: 'Em recebimento' },
  { valor: 'status:concluido', texto: 'Concluídos' },
  /* A fila de quem aprova (0186) — a porta para o trabalho dele. */
  { valor: 'aprovacao:aguardando', texto: 'Aguardando aprovação' },
  { valor: 'aprovacao:aprovados', texto: 'Aprovados' },
  { valor: 'decisao:aceite', texto: 'Aceites' },
  { valor: 'decisao:aceite_com_ressalva', texto: 'Aceites com ressalva' },
  { valor: 'decisao:rejeicao', texto: 'Rejeitados' },
];

/**
 * Há recebida sem ligação E prevista sem amostra (onda 250): o par pode
 * ser o mesmo saco. O nome resolve os casos limpos (`a_ligar`); os outros
 * a pessoa escolhe na janela.
 */
const temOQueLigar = (o: OsEsperando) =>
  (o.a_ligar ?? 0) > 0 || ((o.soltas ?? 0) > 0 && o.faltam > 0);

/**
 * A falta contável, separada em duas (onda 250): o que chegou sem ligação
 * à prevista e o que ainda não chegou. Antes, as duas saíam somadas como
 * «ainda não chegaram».
 */
function Falta({ o }: { o: OsEsperando }) {
  const aLigar = Math.min(o.a_ligar ?? 0, o.faltam);
  const naoChegaram = o.faltam - aLigar;
  const partes: string[] = [];
  if (aLigar > 0) {
    partes.push(`${aLigar} ${aLigar === 1 ? 'chegou' : 'chegaram'} sem ligação à prevista`);
  }
  if (naoChegaram > 0) {
    partes.push(`${naoChegaram} de ${o.previstas} `
      + `${o.previstas === 1 ? 'prevista' : 'previstas'} ainda `
      + `${naoChegaram === 1 ? 'não chegou' : 'não chegaram'}`);
  }
  if (aLigar === 0 && (o.soltas ?? 0) > 0) {
    partes.push(`${plural(o.soltas, 'recebida', 'recebidas')} sem ligação`);
  }
  return <>{partes.join(' · ')}</>;
}

export function Recebimentos() {
  const { pode } = useSessao();
  const navegar = useNavigate();
  const [busca, setBusca] = useState('');
  const [situacao, setSituacao] = useState<string | null>(null);
  const [cliente, setCliente] = useState<string | null>(null);
  const [lotes, setLotes] = useState<Lote[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [lendoQr, setLendoQr] = useState(false);
  const [erroQr, setErroQr] = useState<string | null>(null);
  const [esperando, setEsperando] = useState<OsEsperando[] | null>(null);
  const [verTodas, setVerTodas] = useState(false);
  const [recarga, setRecarga] = useState(0);
  const [ligando, setLigando] = useState<OsEsperando | null>(null);
  const [recargaEspera, setRecargaEspera] = useState(0);
  /* O período da barra: o dia do recebimento (onda 271). */
  const [de, setDe] = useState('');
  const [ate, setAte] = useState('');

  /*
   * O resumo do que FALTA chegar carrega uma vez, e não a cada tecla da
   * busca: a busca filtra os recebimentos JÁ FEITOS, e refazer esta
   * consulta a cada letra digitada seria trabalho de servidor para um
   * bloco que não muda.
   */
  useEffect(() => {
    api<OsEsperando[]>('/recebimento/aguardando')
      .then(setEsperando)
      .catch(() => setEsperando([]));
  }, [recargaEspera]);

  useEffect(() => {
    const abortar = new AbortController();
    const t = setTimeout(() => {
      const q = new URLSearchParams();
      if (busca.trim()) q.set('busca', busca.trim());
      if (situacao) {
        const [campo, valor] = situacao.split(':');
        q.set(campo, valor);
      }
      if (cliente) q.set('cliente', cliente);
      if (de) q.set('de', de);
      if (ate) q.set('ate', ate);
      const qs = q.toString();
      api<Lote[]>(`/recebimento${qs ? `?${qs}` : ''}`, { sinal: abortar.signal })
        .then((r) => { setLotes(r); setErro(null); })
        .catch((e) => { if ((e as Error).name !== 'AbortError') setErro(e); });
    }, busca ? 300 : 0);
    return () => { clearTimeout(t); abortar.abort(); };
  }, [busca, situacao, cliente, de, ate, recarga]);

  /**
   * A etiqueta leva ao recebimento de onde a amostra veio.
   *
   * O QR carrega só o código (`etiqueta.service.ts`), então ler com a câmera
   * do sistema operacional não abre nada. É aqui que o código vira tela — e
   * é este o caminho que confere, depois de imprimir a folha, se a etiqueta
   * colada corresponde à amostra certa.
   *
   * A última leitura vence (onda 308, Ruling R30): a câmera continua lendo
   * depois de um erro, e a etiqueta B pode ser lida enquanto a API ainda
   * responde pela A. Cada leitura pega um número; a resposta de uma leitura
   * que já não é a última é ignorada — senão a A, chegando atrasada,
   * navegaria para a amostra errada. Estável (`useCallback`): o `LeitorQr`
   * reabre a câmera quando o `aoLer` muda, e cada erro mostrado a reabria.
   */
  const leitura = useRef(0);
  const abrirPorCodigo = useCallback(async (codigo: string) => {
    const minha = ++leitura.current;
    setErroQr(null);
    try {
      // Confere que existe antes de navegar: mandar para uma tela que vai
      // dar "não encontrada" deixaria o técnico sem o motivo, com a
      // etiqueta na mão e a câmera já fechada.
      const a = await api<{ codigo: string }>(
        `/amostras/codigo/${encodeURIComponent(codigo)}`);
      if (minha !== leitura.current) return;
      setLendoQr(false);
      navegar(`/amostra/${encodeURIComponent(a.codigo)}`);
    } catch (e) {
      if (minha !== leitura.current) return;
      setErroQr(e instanceof ErroApi ? e.message : `Não achei a amostra ${codigo}.`);
    }
  }, [navegar]);

  /*
   * Fechar a janela (×, Esc, «Cancelar») com a conferência em voo: a
   * resposta que chega depois não pode navegar nem reabrir o erro. Fechar
   * conta como uma leitura nova — a em voo deixa de ser a última, e o
   * `abrirPorCodigo` a ignora.
   */
  const fecharQr = useCallback(() => {
    leitura.current++;
    setLendoQr(false);
  }, []);

  /* A pergunta da lista: mudou, o celular recomeça em 30. */
  const pag = usePaginacao(lotes ?? [], 50,
    [busca.trim(), situacao, cliente, de, ate].join('|'));
  const ativos: FiltroAtivo[] = [];
  if (situacao) ativos.push({ chave: 'sit', texto: `Situação: ${SITUACOES.find((o) => o.valor === situacao)?.texto ?? situacao}`, tirar: () => setSituacao(null) });
  if (cliente) ativos.push({ chave: 'cli', texto: 'Cliente escolhido', tirar: () => setCliente(null) });
  if (busca.trim()) ativos.push({ chave: 'q', texto: `Busca: ${busca.trim()}`, tirar: () => setBusca('') });
  if (de || ate) ativos.push({ chave: 'per', texto: `Recebidos: ${de ? dataCurta(de) : '…'} – ${ate ? dataCurta(ate) : '…'}`, tirar: () => { setDe(''); setAte(''); } });
  /* O «Limpar filtros» da faixa: tudo. */
  const limparFiltros = () => { setSituacao(null); setCliente(null); setBusca(''); setDe(''); setAte(''); };
  /* O número e o «Limpar» da folha do celular (309, Ruling R13): só o que
     mora na folha — a busca está à vista e fica. */
  const naFolha = ativos.filter((f) => ['sit', 'cli', 'per'].includes(f.chave)).length;
  const limparDaFolha = () => { setSituacao(null); setCliente(null); setDe(''); setAte(''); };

  return (
    <div>
      <div className="cabeca cabeca-lista">
        <div className="cresce">
          <h1>Recebimentos</h1>
          <p className="sub">O que chegou ao laboratório e como chegou — o mais recente primeiro.</p>
        </div>
        <button
          type="button"
          className="botao secundario"
          onClick={() => { setErroQr(null); setLendoQr(true); }}
        >
          Ler etiqueta
        </button>
        {pode('amostra.receber') && (
          <Link to="/recebimento/novo" className="botao">+ Receber amostras</Link>
        )}
      </div>

      <BarraDeLista rotulo="Filtros dos recebimentos" ativos={naFolha}
        aoLimpar={limparDaFolha} total={lotes ? lotes.length : undefined}>
        <BuscaDaBarra valor={busca} aoMudar={setBusca}
          placeholder="Código, cliente, obra, OS ou quem entregou"
          rotulo="Buscar recebimento" />
        <Periodo valor={{ de, ate }} rotulo="Recebidos"
          aoMudar={(v) => { setDe(v.de); setAte(v.ate); }}
          atalhos={['hoje', 'semana', 'mes', 'mesPassado', 'ultimos30', 'tudo']} />
        <Seletor naBarra rotulo="Situação" valor={situacao} opcoes={SITUACOES}
          aoMudar={setSituacao} todos="Todas as situações" />
        <SeletorDeCliente naBarra valor={cliente} aoMudar={setCliente} />
      </BarraDeLista>

      <FiltrosAtivos itens={ativos} aoLimpar={limparFiltros}
        contagem={lotes ? `${lotes.length} ${lotes.length === 1 ? 'recebimento' : 'recebimentos'}` : undefined} />

      <ErroDaApi erro={erro} />

      {/*
        * O que ainda NÃO chegou, antes do que já chegou.
        *
        * A tela listava só os recebimentos feitos — a metade errada para
        * quem está com a carga na mão. Quem descarrega o caminhão precisa
        * saber de qual OS é aquele material, e é esta lista que responde.
        */}
      {esperando && esperando.length > 0 && (
        <section className="cartao esperando-amostra">
          <h2>
            Esperando amostra
            <span className="contador">{esperando.length}</span>
          </h2>
          <p className="sub">
            Ordens de serviço abertas cujo material ainda não chegou ao
            laboratório. Ao descarregar, comece por aqui.
          </p>
          <div className="lista">
            {(verTodas ? esperando : esperando.slice(0, 6)).map((o) => (
              <div key={o.os_id} className="item linha-topo">
                <div className="cresce">
                  <Link to={`/os/${encodeURIComponent(o.os_id)}`} className="cod">
                    OS {o.os_numero}
                  </Link>
                  <div className="sub">
                    {clienteEObra(o.cliente, o.obra_nome).join(' · ')
                      || <span className="nada">sem cliente</span>}
                  </div>
                  <div className="meta">
                    {/*
                      * Os dois motivos dizem coisas diferentes, e a view
                      * os separa em vez de somar: «nada chegou» pode ser
                      * OS de campo que nunca terá amostra de laboratório;
                      * «faltam 7 de 12» é falta contável, com número.
                      */}
                    {o.motivo === 'sem_nenhuma'
                      ? 'nenhuma amostra recebida'
                      : <Falta o={o} />}
                    {' · aberta há '}{plural(o.dias_aberta, 'dia', 'dias')}
                    {o.dias_para_prazo !== null && (
                      <span className={o.dias_para_prazo < 0 ? 'aviso-linha' : undefined}>
                        {o.dias_para_prazo < 0
                          ? ` · ${plural(-o.dias_para_prazo, 'dia', 'dias')} de atraso`
                          : ` · ${plural(o.dias_para_prazo, 'dia', 'dias')} para o prazo`}
                      </span>
                    )}
                  </div>
                </div>
                {pode('amostra.receber') && temOQueLigar(o) && (
                  <button
                    type="button"
                    className="botao estreito"
                    onClick={() => setLigando(o)}
                  >
                    Ligar às previstas
                  </button>
                )}
                {pode('amostra.receber') && (
                  <Link
                    to={`/recebimento/novo?os=${encodeURIComponent(o.os_id)}`}
                    className="botao secundario estreito"
                  >
                    Receber amostras
                  </Link>
                )}
              </div>
            ))}
          </div>
          {esperando.length > 6 && !verTodas && (
            <button type="button" className="como-link"
                    onClick={() => setVerTodas(true)}>
              ver as outras {esperando.length - 6}
            </button>
          )}
        </section>
      )}

      <Dialogo
        aberto={!!ligando}
        titulo={ligando ? `Ligar às previstas · OS ${ligando.os_numero}` : 'Ligar às previstas'}
        aoFechar={() => setLigando(null)}
      >
        {ligando && (
          <LigarPrevistas
            osId={ligando.os_id}
            aoLigar={() => setRecargaEspera((n) => n + 1)}
          />
        )}
      </Dialogo>

      {lotes === null && !erro && <Carregando texto="Carregando recebimentos" />}

      {lotes && lotes.length === 0 && (
        <section className="cartao">
          <Vazio>
            {ativos.length > 0
              ? 'Nenhum recebimento com estes filtros. Use «Limpar filtros» para ver todos.'
              : 'Nenhum recebimento registrado ainda. Comece por «+ Receber amostras».'}
          </Vazio>
        </section>
      )}

      {lotes && lotes.length > 0 && (
        /*
         * Os recebimentos numa tabela com ficha (onda 219).
         *
         * Era uma pilha de cartões: para saber quem entregou ou quantas
         * fotos faltam, abria-se o lote. Agora a tabela mostra o que se
         * procura (a OS, o cliente, a situação) e o resto — data, quem
         * entregou, amostras e fotos — fica numa coluna que, com a ficha
         * aberta, passa a morar nela. O número continua sendo o link para
         * o recebimento.
         */
        <section className="cartao">
          <TabelaComFicha
            linhas={pag.visiveis}
            chave={(l) => l.id}
            nome={(l) => (l.os_numero ? `OS ${l.os_numero}` : `lote ${l.codigo}`)}
            rotulo="Recebimentos"
            colunas={COLUNAS_DO_RECEBIMENTO}
            ficha={fichaDoRecebimento}
            lixeira={{ tipo: 'recebimento', id: (l) => l.id,
              rotulo: (l) => `recebimento ${l.codigo}`,
              aoApagar: () => setRecarga((n) => n + 1) }}
            cartao={(l) => <CartaoDoLote l={l} />}
            destino={(l) => `/recebimento/${l.id}`}
          />
          <RodapeDaLista {...pag} teto={300} ordem="recebido em ↓" />
        </section>
      )}

      <Dialogo aberto={lendoQr} titulo="Ler etiqueta" aoFechar={fecharQr}>
        {erroQr && <div className="aviso erro" role="alert">{erroQr}</div>}
        {lendoQr && <LeitorQr aoLer={abrirPorCodigo} aoCancelar={fecharQr} />}
      </Dialogo>
    </div>
  );
}

/**
 * O cartão do recebimento no celular (309, spec 06 §6 item 8): o lote e a
 * situação, o cliente e a OS, quando chegou e quantas amostras — o que se
 * confere com a carga na mão. O toque leva ao lote.
 */
function CartaoDoLote({ l }: { l: Lote }) {
  const amostras = Number(l.amostras);
  return (
    <>
      <div className="cartao-l1">
        <span className="mono">{l.codigo}</span>
        <span className="cartao-selos">
          <span className={`selo ${l.decisao}`}>{rotulo(l.decisao)}</span>
        </span>
      </div>
      {/* O cliente é leitura (16 px); a OS, apoio (14 px) — RN-251 i. */}
      <span className="cartao-cli">
        {l.cliente ?? <span className="nada">sem cliente</span>}
        {' · '}
        <span className="cartao-os">
          {l.os_numero ? `OS ${l.os_numero}` : <span className="aviso-linha">sem OS</span>}
        </span>
      </span>
      <span className="cartao-meta">
        {dataHora(l.datahora_recebimento)} · {plural(amostras, 'amostra', 'amostras')}
      </span>
      {/* O lote fechado que espera assinatura parecia pronto (0186). */}
      {l.status === 'concluido' && !l.aprovado && (
        <span className="cartao-meta aviso-linha">aguardando aprovação</span>
      )}
    </>
  );
}

/** Quantas amostras e como estão as fotos — na coluna e na ficha. */
function AmostrasDoLote({ l }: { l: Lote }) {
  const amostras = Number(l.amostras);
  const comRoteiro = Number(l.amostras_com_roteiro);
  const semMinimo = Number(l.amostras_sem_minimo ?? 0);
  return (
    <>
      {plural(amostras, 'amostra', 'amostras')}
      {/* «fotos em 0/2» com quatro fotos no lote (UX-065): a conta era do
          ROTEIRO completo (4 itens), e o mínimo exigido são 2. Diz o que o
          banco cobra. */}
      {amostras > 0 && (
        <span className="meta">
          fotos: {amostras - semMinimo}/{amostras} com o mínimo
          {comRoteiro > 0 && comRoteiro < amostras
            && ` (${comRoteiro} com roteiro completo)`}
        </span>
      )}
    </>
  );
}

/** A decisão, e o que espera assinatura dito na linha (0186). */
function SituacaoDoLote({ l }: { l: Lote }) {
  return (
    <>
      <span className={`selo ${l.decisao}`}>{rotulo(l.decisao)}</span>
      {/*
        * Rascunho já se anuncia pelo selo da decisão e pela conta de
        * fotos. O que não se via era o lote fechado esperando aprovação —
        * ele parecia pronto, e o relatório não saía.
        */}
      {l.status === 'concluido' && !l.aprovado && (
        <span className="meta aviso-linha">aguardando aprovação</span>
      )}
    </>
  );
}

/*
 * A OS na frente, o lote atrás.
 *
 * "Aqui em recebimento de amostras, o que mais importa é o número da OS e
 *  não a nomenclatura do lote de amostras recebido" (Testesolo, 23/08/2026).
 * O lote é um número interno que só existe dentro desta tela; a OS é o que
 * o cliente cita ao telefone. Lote órfão continua aparecendo — e aparecendo
 * COMO órfão, que é o que precisa de ação.
 */
const COLUNAS_DO_RECEBIMENTO: ColunaComFicha<Lote>[] = [
  {
    chave: 'os',
    cabeca: <th scope="col">OS</th>,
    celula: (l) => (
      <th scope="row">
        <Link to={`/recebimento/${l.id}`} className="cod">
          {l.os_numero ? `OS ${l.os_numero}` : <span className="nada">sem OS</span>}
        </Link>
        <span className="sub mono lote">lote {l.codigo}</span>
      </th>
    ),
  },
  {
    chave: 'cliente',
    cabeca: <th scope="col">Cliente e obra</th>,
    celula: (l) => {
      const [c, o] = clienteEObra(l.cliente, l.obra_nome);
      return (
        <td>
          {c ?? <span className="nada">sem cliente</span>}
          {o && <span className="sub">{o}</span>}
        </td>
      );
    },
  },
  {
    chave: 'quando', detalhe: true,
    cabeca: <th scope="col" className="col-opcional">Recebido em</th>,
    celula: (l) => <td className="col-opcional mono">{dataHora(l.datahora_recebimento)}</td>,
  },
  {
    chave: 'entrega', detalhe: true,
    cabeca: <th scope="col" className="col-opcional">Entregue por</th>,
    celula: (l) => (
      <td className="col-opcional">
        {l.responsavel_entrega_nome ?? <span className="nada">—</span>}
      </td>
    ),
  },
  {
    /* Sem `col-opcional`: no celular a quantidade de amostras é o que se
       confere com a carga na mão (o cartão antigo a mostrava — 10.11). */
    chave: 'amostras', detalhe: true,
    cabeca: <th scope="col">Amostras</th>,
    celula: (l) => <td><AmostrasDoLote l={l} /></td>,
  },
  {
    chave: 'situacao',
    cabeca: <th scope="col">Situação</th>,
    celula: (l) => <td><SituacaoDoLote l={l} /></td>,
  },
];

/** A ficha do recebimento: o que as colunas recolhidas diziam, e o PDF. */
function fichaDoRecebimento(l: Lote): FichaDaLinha {
  const [c, o] = clienteEObra(l.cliente, l.obra_nome);
  const esperaAprovacao = l.status === 'concluido' && !l.aprovado;
  return {
    rotulo: 'Recebimento',
    titulo: l.os_numero ? `OS ${l.os_numero}` : `lote ${l.codigo}`,
    tituloCodigo: true,
    sub: (
      <>
        {c && <span>{c}</span>}
        {o && <span>{o}</span>}
      </>
    ),
    corpo: (
      <>
        <div className="tf-selos">
          <span className={`selo ${l.decisao}`}>{rotulo(l.decisao)}</span>
          <span className="selo neutro">{rotulo(l.status)}</span>
        </div>
        {esperaAprovacao && (
          <div className="tf-aviso">
            <strong>Aguarda aprovação.</strong>{' '}
            Quem recebeu não aprova o próprio recebimento.
          </div>
        )}
        {!l.os_numero && (
          <div className="tf-aviso critico">
            <strong>Lote sem OS.</strong> Amarre-o a uma ordem de serviço.
          </div>
        )}
        <CamposDaFicha itens={[
          ['Lote', <span className="mono">{l.codigo}</span>],
          ['Recebido em', dataHora(l.datahora_recebimento)],
          ['Entregue por', l.responsavel_entrega_nome],
          ['Amostras', <AmostrasDoLote l={l} />],
          ['Relatório', l.pdf_revisao > 0
            ? `PDF R${String(l.pdf_revisao).padStart(2, '0')}` : null],
        ]} />
      </>
    ),
    acoes: (
      <>
        {l.os_numero && (
          <Link className="botao secundario"
            to={`/os/${encodeURIComponent(l.os_numero)}`}>Ver OS</Link>
        )}
        <Link className="botao" to={`/recebimento/${l.id}`}>Abrir recebimento</Link>
      </>
    ),
  };
}

/** `2026-10-03` → `03/10/2026`, para a etiqueta do filtro. */
function dataCurta(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
}
